import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { ShareLink } from '@mockd/shared';
import { getApiBaseUrl } from '../config';

interface UseShareLinkReturn {
  shareLink: ShareLink | null;
  loading: boolean;
  fetchShareLink: (projectId: string) => Promise<void>;
  createShareLink: (projectId: string) => Promise<ShareLink>;
  deleteShareLink: (projectId: string) => Promise<void>;
}

export function useShareLink(): UseShareLinkReturn {
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchShareLink = useCallback(async (projectId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}/share-link`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch share link');
      }
      const json = await response.json();
      setShareLink(json.data || null);
    } catch {
      setShareLink(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const createShareLink = useCallback(async (projectId: string): Promise<ShareLink> => {
    const response = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}/share-link`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      const json = await response.json().catch(() => ({}));
      const errorMsg = json.error || 'Failed to create share link';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    const json = await response.json();
    const link = json.data as ShareLink;
    setShareLink(link);
    toast.success('Share link created');
    return link;
  }, []);

  const deleteShareLink = useCallback(async (projectId: string): Promise<void> => {
    const response = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}/share-link`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      toast.error('Failed to revoke share link');
      throw new Error('Failed to revoke share link');
    }

    setShareLink(null);
    toast.success('Share link revoked');
  }, []);

  return {
    shareLink,
    loading,
    fetchShareLink,
    createShareLink,
    deleteShareLink,
  };
}
